import { Box, Card, IconButton, useTheme } from '@mui/material'

import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import { useNavigate } from 'react-router-dom'
import CustomButton from '../../../../Components/CustomButton/CustomButton'
import WTypography from '../../../../Components/whiteTypography'
import { AppContext } from '../../../../context'
import GEmissionBarChart from '../Charts/GEmissionBarChart'
import HorizentalBarChart from '../Charts/HorizentalBarChart'
import DonutChartEmission from '../Charts/EmissionDonut'
import BarCIcone from '../Icone/bar_chart_white_24dp.svg'
import DonutCIcone from '../Icone/donut_small_white_24dp.svg'
// Format emission value for display (sum of each card); never show blank
const formatEmission = (value) => (parseFloat(value) ?? 0).toFixed(2)

// LAP-752 — French per-capita CO2 footprint, source: ADEME 2022 (9.2 t CO2e/yr).
// Surfaced in the carbon footprint highlight so users can compare their own
// total emission to the national average.
const FR_AVG_CO2_TONS_PER_YEAR = 9.2
// LAP-752 — Per-capita target consistent with the Paris Agreement +1.5°C
// trajectory by 2050. We surface it as an aspirational benchmark so users
// see where their lifestyle is vs both the current French average and the
// long-term climate goal.
const PARIS_2050_TARGET_TONS = 2.0

const compareToNationalAverage = (userTotal) => {
  const user = parseFloat(userTotal || 0)
  if (!user) return { diff: 0, ratio: 0, label: 'no_data' }
  const diff = user - FR_AVG_CO2_TONS_PER_YEAR
  const ratio = (user / FR_AVG_CO2_TONS_PER_YEAR) * 100
  return {
    diff: Number(diff.toFixed(2)),
    ratio: Number(ratio.toFixed(0)),
    label: diff < 0 ? 'below_average' : diff > 0 ? 'above_average' : 'equal_average',
  }
}

function GEmissionChart({ oldData }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const navigate = useNavigate() // LAP-752 — for the new "Créer un objectif" CTA
  const [optionChart, setOptionChart] = useState(false)
  const [pour, setPour] = useState(0.0)
  const [pour1, setPour1] = useState(0.0)
  const [sumFootprint, setSumFootprint] = useState(0.0)
  const [objectiveActive, setObjectiveActive] = useState(false)
  const totalEmissionDisplay = oldData?.[0] != null ? formatEmission(oldData[0].emission_total) : '0.00'

  useEffect(() => {
    if (oldData && oldData.length > 0) {
      const data = oldData[0]
      const isActive = data?.objective_active === true
      setObjectiveActive(isActive)
      
      // Only calculate if objective is active
      if (isActive) {
        const val = parseFloat(data?.pourcentage || 0)
        const totalEmission = parseFloat(data?.emission_total || 0)
        const reducedEmission = totalEmission - (totalEmission * val) / 100
        const sum = totalEmission + reducedEmission

        setPour(parseFloat(val).toFixed(2))
        setPour1(parseFloat(reducedEmission).toFixed(2))
        setSumFootprint(parseFloat(sum).toFixed(2))
      } else {
        // If objective is not active, set to 0
        setPour(0.0)
        setPour1(parseFloat(data?.emission_total || 0).toFixed(2))
        setSumFootprint(parseFloat(data?.emission_total || 0).toFixed(2))
      }
    }
  }, [oldData])
  return (
    <Card className="dashboard_card__root1">
      {oldData && oldData.length > 0 ? (
        <>
          {optionChart ? (
            <>
              <Box className="dashboard_card__header1">
                <WTypography component={'span'} variant="h6">
                  {t('dashboard.emissionGlobal') /* LAP-733 (GE022) */}
                </WTypography>
                <WTypography component={'span'} variant="h6">
                  {totalEmissionDisplay}
                  <WTypography
                    component={'span'}
                    sx={{ fontSize: '12px', color: 'white', marginLeft: '4px' }}
                  >
                    t. CO<sup>2</sup>/an
                  </WTypography>
                </WTypography>
              </Box>
              <Box mt={3} minWidth={3} mb={6} height={'100%'}>
                <GEmissionBarChart reports={oldData} />
              </Box>
              {oldData && (
                <Box mt={2} className="dashboard_card__filters1">
                  <Box className="no-drag">
                    <IconButton onClick={() => setOptionChart(false)}>
                      <img src={DonutCIcone} alt="DonutChart" className="Optionbutton"></img>
                    </IconButton>
                    {/* <IconButton onClick={() => setOptionChart(true)}>
                    <img src={BarCIcone} alt="BarChart" className="Optionbutton"></img>
                  </IconButton> */}
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <div className="main_div">
              <div className="box_div">
                <Box className="dashboard_card__header1">
                  <WTypography component={'span'} variant="h6">
                    {t('dashboard.emissionGlobal') /* LAP-733 (GE022) */}
                  </WTypography>
                  <WTypography component={'span'} variant="h6">
                    {totalEmissionDisplay}
                    <WTypography
                      component={'span'}
                      sx={{ fontSize: '12px', color: 'white', marginLeft: '4px' }}
                    >
                      t. CO<sup>2</sup>/an
                    </WTypography>
                  </WTypography>
                </Box>
                <Box mt={5} className="dashboard_card__header1">
                  <WTypography component={'span'} variant="h7">
                    {t('dashboard.objectiveGlobal') /* LAP-733 (GE022) */}
                  </WTypography>
                  <WTypography component={'span'} variant="h6">
                    {objectiveActive ? pour1 : totalEmissionDisplay}
                    <WTypography
                      component={'span'}
                      sx={{ fontSize: '12px', color: 'white', marginLeft: '4px' }}
                    >
                      t co<sup>2</sup>/an.
                    </WTypography>
                  </WTypography>
                </Box>
                {/* LAP-752 — National average comparison */}
                <Box mt={3} className="dashboard_card__header1">
                  <WTypography component={'span'} variant="h7">
                    {t('dashboard.frenchAverage', 'Moyenne française')}
                  </WTypography>
                  <WTypography component={'span'} variant="h6">
                    {(() => {
                      const cmp = compareToNationalAverage(totalEmissionDisplay)
                      const sign = cmp.diff > 0 ? '+' : ''
                      return `${FR_AVG_CO2_TONS_PER_YEAR} t (${sign}${cmp.diff})`
                    })()}
                  </WTypography>
                </Box>
                {/* LAP-752 — Paris Agreement 2050 target reference */}
                <Box mt={2} className="dashboard_card__header1">
                  <WTypography
                    component={'span'}
                    variant="h7"
                    sx={{ color: 'rgba(255,255,255,0.6)' }}
                  >
                    {t('dashboard.paris2050', 'Objectif Accords de Paris 2050')}
                  </WTypography>
                  <WTypography
                    component={'span'}
                    variant="h6"
                    sx={{ color: '#f7b955' }}
                  >
                    {PARIS_2050_TARGET_TONS} t
                  </WTypography>
                </Box>
                <Box mt={4} className="dashboard_card__header1">
                  <WTypography component={'span'} variant="h7">
                    {t('dashboard.objectivePercent') /* LAP-733 (GE022) */}
                  </WTypography>
                  {objectiveActive ? (
                    <WTypography component={'span'} variant="h6">
                      {`${pour} %`}
                    </WTypography>
                  ) : (
                    // LAP-752 — Replace the dull "No active objective" text
                    // with a real call-to-action. Clicking lands the user on
                    // /mesobjectifs where they can create one (or wait for
                    // the eco recommendation Activer button to do it for
                    // them with the AI-suggested %).
                    <CustomButton
                      className="no-drag"
                      onClick={() => navigate('/mesobjectifs')}
                      sx={{
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '4px 12px',
                        minWidth: 0,
                        background: 'rgba(47,210,138,0.18)',
                        color: '#b6f0d4',
                        border: '1px solid rgba(47,210,138,0.4)',
                        borderRadius: '999px',
                        '&:hover': {
                          background: 'rgba(47,210,138,0.30)',
                          borderColor: '#2fd28a',
                        },
                      }}
                    >
                      + {t('dashboard.createObjectiveCta', 'Créer un objectif')}
                    </CustomButton>
                  )}
                </Box>
                
                {/* Sum of footprint + reduced footprint in red (only show if objective is active) */}
                {objectiveActive && (
                  <Box mt={4} className="dashboard_card__header1">
                    <WTypography component={'span'} variant="h7">
                      {t('dashboard.totalEmissionsObjective') /* LAP-733 (GE022) */}
                    </WTypography>
                    <WTypography 
                      component={'span'} 
                      variant="h6"
                      sx={{ color: '#ff4444' }} // Red color
                    >
                      {sumFootprint}
                      <WTypography
                        component={'span'}
                        sx={{ fontSize: '12px', color: '#ff4444', marginLeft: '4px' }}
                      >
                        t. CO<sup>2</sup>/an
                      </WTypography>
                    </WTypography>
                  </Box>
                )}

                <Box mt={3} minWidth={3} mb={6} height={'100%'} className="no-drag">
                  <HorizentalBarChart reports={oldData} />
                </Box>
              </div>
              <div className="box_div">
                <Box className="dashboard_card__header2">
                  <WTypography component={'span'} variant="h6">
                    {t('dashboard.distributionByCategory') /* LAP-733 (GE022) */}
                  </WTypography>
                </Box>

                <Box mt={10} className="chart_box" minWidth={3} mb={6} height={'100%'}>
                  <DonutChartEmission reports={oldData} />
                </Box>

                {oldData && (
                  <Box mt={2} className="dashboard_card__filters1">
                    <Box className="no-drag">
                      {/* <IconButton onClick={() => setOptionChart(false)}>
                    <img src={DonutCIcone} alt="DonutChart" className="Optionbutton"></img>
                  </IconButton> */}
                      <IconButton onClick={() => setOptionChart(true)}>
                        <img src={BarCIcone} alt="BarChart" className="Optionbutton"></img>
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <Box className="">
          <WTypography component={'span'} variant="h6">
            {t('dashboard.emissionGlobal') /* LAP-733 (GE022) */}
          </WTypography>
          <WTypography variant="h6" align="center" marginTop="10rem">
            {t('dashboard.noData') /* LAP-733 (GE022) */}
          </WTypography>
        </Box>
      )}
    </Card>
  )
}

export default GEmissionChart
